import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { licencesApi, errorOf, TYPE_LABEL, nextWords, shortDay } from '../../lib/licences';
import { useConfirm } from '../ConfirmDialog';
import './Licences.css';

/**
 * THE LICENCES A PERSON OR A DEVICE HOLDS (119) - on a device's Overview,
 * and on a person's own licences page: each live seat with a link to its
 * licence and Free, a seat of another licence given from here, and (for a
 * person) "Free all their seats". Administrators only, like the register;
 * the parent decides whether to draw it at all.
 *
 *   kind     'user' | 'asset'
 *   id       the person's or the device's id
 *   compact  the device card: a heading of its own, no history
 */
export default function HolderLicences({ kind, id, compact = false, onChange = null, card = false }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [problem, setProblem] = useState('');
  const [choices, setChoices] = useState(null);
  const [pick, setPick] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirm, confirmDialog] = useConfirm();

  const load = useCallback(() => (kind === 'user' ? licencesApi.person(id) : licencesApi.device(id))
    .then((d) => {
      // An answer that is not the register's (a proxy page, an old server):
      // as good as not there.
      if (!d || !Array.isArray(d.seats)) { setError('hide'); return; }
      setData(d); setError('');
    })
    .catch((e) => {
      // Not set up here yet (503, before 119) or switched off (403): say
      // nothing - the card is an extra, and the device page is not broken.
      if ([403, 503].includes(e?.response?.status) || (card && e?.response?.status === 404)) { setError('hide'); return; }
      setError(errorOf(e, 'Could not load the licences.'));
    }), [kind, id, card]);
  useEffect(() => { load(); }, [load]);

  // The licences a seat could come from: active, the right type, not held.
  useEffect(() => {
    let live = true;
    licencesApi.list({ status: 'active', sort: 'product' })
      .then((r) => { if (live) setChoices(Array.isArray(r?.licences) ? r.licences : []); })
      .catch(() => { if (live) setChoices([]); });
    return () => { live = false; };
  }, []);

  const changed = async () => { await load(); if (onChange) onChange(); };

  const free = async (s) => {
    const name = kind === 'user' ? data?.person?.name : data?.asset?.asset_tag;
    if (!(await confirm({ title: `Free the seat of ${s.product}?`, message: `${name} will no longer hold it. The seat goes back to the licence.`, confirmLabel: 'Free the seat' }))) return;
    setProblem('');
    try {
      await licencesApi.unassign(s.id, s.assignment_id);
      await changed();
    } catch (e) {
      setProblem(errorOf(e, 'The seat could not be freed.'));
    }
  };

  const freeAll = async () => {
    const n = data?.seats?.length || 0;
    if (!(await confirm({ title: `Free all ${n} of ${data.person.name}’s seats?`, message: 'Each goes back to its licence, and the history keeps who had it.', confirmLabel: 'Free them all', danger: true }))) return;
    setProblem('');
    try {
      await licencesApi.freePerson(id, { reason: 'freed from their licences page' });
      await changed();
    } catch (e) {
      setProblem(errorOf(e, 'The seats could not be freed.'));
    }
  };

  const give = async (allowOver = false) => {
    if (!pick) return;
    setBusy(true);
    setProblem('');
    try {
      await licencesApi.assign(pick, kind === 'user' ? { user_id: id, allow_over: allowOver } : { asset_id: id, allow_over: allowOver });
      setPick('');
      await changed();
    } catch (e) {
      const d = e?.response?.data;
      if (e?.response?.status === 409 && d?.needs_confirm && !allowOver) {
        setBusy(false);
        if (await confirm({ title: 'All the seats are in use', message: d.error, confirmLabel: 'Give it anyway' })) await give(true);
        return;
      }
      setProblem(errorOf(e, 'The seat could not be given.'));
    }
    setBusy(false);
  };

  // `card`: drawn as its own panel (the device page), and not at all when
  // the register is not there for this administrator.
  if (error === 'hide') return card ? null : <p className="as-quiet">Licences are not available here.</p>;
  const wrap = (body) => (card ? (
    <section className="as-panel" data-card="licences" aria-label="Licences" style={{ marginBottom: '1.25rem' }}>
      <h2>Licences</h2>
      {body}
    </section>
  ) : body);
  if (error) return wrap(<p className="as-error" role="alert">{error}</p>);
  if (!data) return wrap(<p className="as-quiet" aria-busy="true">Loading the licences…</p>);

  const held = new Set(data.seats.map((s) => s.id));
  // Nothing new for somebody switched off, or a device that has gone.
  const canGive = kind === 'user' ? data.person?.active !== false : !['retired', 'disposed'].includes(data.asset?.lifecycle_status);
  const offer = (canGive ? choices || [] : []).filter((l) => !held.has(l.id)
    && (kind === 'user' ? l.licence_type !== 'per_device' : l.licence_type !== 'per_user'));

  return wrap(
    <div data-testid={`holder-licences-${kind}`}>
      {data.seats.length === 0 ? (
        <p className="as-quiet" data-testid="no-licences">
          {kind === 'user' ? 'No licence seats.' : 'No licences on this device.'}
        </p>
      ) : (
        <ul className="lic-seats" aria-label="Licences held">
          {data.seats.map((s) => {
            const nx = nextWords(s);
            return (
              <li key={s.assignment_id} data-licence={s.id}>
                <span className="lic-seat-who">
                  <Link to={`/assets/licences/${s.id}`}>{s.product}</Link>
                  <span className="lic-seat-sub">
                    {[s.vendor, TYPE_LABEL[s.licence_type], `since ${shortDay(s.assigned_at)}`].filter(Boolean).join(' · ')}
                  </span>
                </span>
                {nx && <span className="as-chip" data-tone={nx.tone}>{nx.text}</span>}
                {s.over_allocated && <span className="as-chip" data-tone="critical">Over-allocated</span>}
                <button type="button" className="btn btn-secondary btn-small" onClick={() => free(s)}
                  aria-label={`Free the seat of ${s.product}`}>Free</button>
              </li>
            );
          })}
        </ul>
      )}

      {offer.length > 0 && (
        <div className="lic-give">
          <label className="lic-give-field">
            {kind === 'user' ? 'Give them a licence' : 'Give this device a licence'}
            <select value={pick} onChange={(e) => setPick(e.target.value)} aria-label="Licence to give">
              <option value="">Choose a licence…</option>
              {offer.map((l) => (
                <option key={l.id} value={l.id}>
                  {`${l.product}${l.seats !== null ? ` (${l.seats_used} of ${l.seats})` : ''}`}
                </option>
              ))}
            </select>
          </label>
          <button type="button" className="btn btn-primary" disabled={!pick || busy} onClick={() => give(false)}>
            {busy ? 'Giving…' : 'Give a seat'}
          </button>
        </div>
      )}
      {kind === 'user' && data.seats.length > 1 && (
        <p><button type="button" className="btn btn-danger btn-small" onClick={freeAll}>Free all their seats</button></p>
      )}
      {problem && <p className="as-error" role="alert">{problem}</p>}

      {!compact && (data.past || []).length > 0 && (
        <>
          <h3 className="lic-seat-kind" style={{ marginTop: '1rem' }}>Held before</h3>
          <ul className="lic-seats" aria-label="Licences held before">
            {data.past.map((s) => (
              <li key={s.assignment_id}>
                <span className="lic-seat-who">
                  <Link to={`/assets/licences/${s.id}`}>{s.product}</Link>
                  <span className="lic-seat-sub">
                    {`${shortDay(s.assigned_at)} to ${shortDay(s.freed_at)}${s.freed_reason ? ` · ${s.freed_reason}` : ''}`}
                  </span>
                </span>
              </li>
            ))}
          </ul>
        </>
      )}
      {confirmDialog}
    </div>
  );
}
